import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Download, 
  Printer, 
  Share2, 
  FileText, 
  User, 
  Target,
  CheckCircle,
  Star,
  Calendar,
  ArrowRight,
  ExternalLink,
  Copy,
  QrCode,
  Briefcase
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface LinkedInProfessionalReportProps {
  profileData: any;
  rawContentData: any;
  onBack: () => void;
}

export default function LinkedInProfessionalReport({ 
  profileData, 
  rawContentData, 
  onBack 
}: LinkedInProfessionalReportProps) {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      // Here you would integrate with a PDF generation service
      // For now, we'll use the browser's print functionality
      window.print();
      toast({
        title: "Report Ready",
        description: "Use your browser's print dialog to save as PDF",
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Unable to export report. Please try printing instead.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyContent = async (content: string, section: string) => {
    try {
      await navigator.clipboard.writeText(content);
      toast({
        title: "Content Copied",
        description: `${section} has been copied to your clipboard`,
      });
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Unable to copy content to clipboard",
        variant: "destructive",
      });
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const generatedContent = profileData?.generated_profile || {};
  const headline = generatedContent.headline || 'Professional LinkedIn Headline';
  const about = generatedContent.about || 'Professional LinkedIn About Section';
  const experience = generatedContent.experience || [];
  const skills = generatedContent.skills || [];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Print-friendly header - only visible when printing */}
      <div className="print:block hidden">
        <div className="text-center mb-8 pb-4 border-b-2 border-primary">
          <h1 className="text-3xl font-bold text-primary mb-2">LinkedIn Profile Optimization Report</h1>
          <p className="text-lg text-muted-foreground">Professional LinkedIn Profile Enhancement</p>
          <p className="text-sm text-muted-foreground mt-2">Generated on {formatDate(new Date())}</p>
        </div>
      </div>

      {/* Screen header - hidden when printing */}
      <div className="print:hidden mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">LinkedIn Profile Report</h1>
            <p className="text-muted-foreground">Your professional LinkedIn optimization report</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button onClick={handleExportPDF} disabled={isExporting}>
              <Download className="w-4 h-4 mr-2" />
              {isExporting ? 'Exporting...' : 'Export PDF'}
            </Button>
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      <Card className="mb-8">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <Star className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl">Executive Summary</CardTitle>
              <CardDescription>Key highlights of your LinkedIn profile optimization</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-success" />
              </div>
              <h3 className="font-semibold mb-1">Profile Optimized</h3>
              <p className="text-sm text-muted-foreground">Complete LinkedIn profile with professional content</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Target className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-1">Keyword Optimized</h3>
              <p className="text-sm text-muted-foreground">Strategic keywords for better discoverability</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <User className="w-8 h-8 text-accent" />
              </div>
              <h3 className="font-semibold mb-1">Brand Aligned</h3>
              <p className="text-sm text-muted-foreground">Content aligned with your professional brand</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* LinkedIn Profile Content */}
      <div className="space-y-6 mb-8">
        {/* Headline */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                LinkedIn Headline
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopyContent(headline, 'Headline')}
              >
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/30 p-4 rounded-lg">
              <p className="font-medium text-lg leading-relaxed">{headline}</p>
            </div>
          </CardContent>
        </Card>

        {/* About Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                About Section
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopyContent(about, 'About Section')}
              >
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/30 p-4 rounded-lg">
              <p className="whitespace-pre-wrap leading-relaxed">{about}</p>
            </div>
          </CardContent>
        </Card>

        {/* Experience Highlights */}
        {experience.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Experience Highlights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {experience.map((exp: any, index: number) => (
                  <div key={index} className="bg-muted/30 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">{exp.title || `Experience ${index + 1}`}</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyContent(exp.description, `Experience ${index + 1}`)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{exp.company}</p>
                    <p className="whitespace-pre-wrap">{exp.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Skills */}
        {skills.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Recommended Skills
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {skills.map((skill: string, index: number) => (
                  <Badge key={index} variant="secondary" className="text-sm">
                    {skill}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Implementation Guide */}
      <Card className="mb-8">
        <CardHeader className="bg-gradient-to-r from-accent/10 to-primary/10">
          <CardTitle className="flex items-center gap-2">
            <ArrowRight className="w-5 h-5" />
            Implementation Guide
          </CardTitle>
          <CardDescription>Step-by-step instructions to update your LinkedIn profile</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold">1</div>
              <div>
                <h4 className="font-semibold mb-1">Update Your Headline</h4>
                <p className="text-sm text-muted-foreground mb-2">Navigate to your LinkedIn profile and click "Edit" next to your headline.</p>
                <p className="text-xs text-muted-foreground">Copy the optimized headline from above and paste it into the headline field.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold">2</div>
              <div>
                <h4 className="font-semibold mb-1">Replace Your About Section</h4>
                <p className="text-sm text-muted-foreground mb-2">Click "Edit" in your About section and replace with the optimized content.</p>
                <p className="text-xs text-muted-foreground">Make sure to maintain paragraph breaks for better readability.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold">3</div>
              <div>
                <h4 className="font-semibold mb-1">Add Recommended Skills</h4>
                <p className="text-sm text-muted-foreground mb-2">Go to your Skills section and add the recommended skills listed above.</p>
                <p className="text-xs text-muted-foreground">Focus on the top 10-15 most relevant skills for your industry.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center text-white text-sm font-bold">4</div>
              <div>
                <h4 className="font-semibold mb-1">Review and Publish</h4>
                <p className="text-sm text-muted-foreground mb-2">Review all changes for accuracy and publish your updated profile.</p>
                <p className="text-xs text-muted-foreground">Consider posting an update to let your network know about your profile refresh.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Raw Content Appendix - Print Only */}
      <div className="print:block hidden">
        <Card>
          <CardHeader>
            <CardTitle>Content Sources & Raw Data</CardTitle>
            <CardDescription>Reference information used in profile optimization</CardDescription>
          </CardHeader>
          <CardContent>
            {rawContentData && (
              <div className="space-y-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-2">Form Responses:</h4>
                  <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(rawContentData.formResponses, null, 2)}</pre>
                </div>
                
                {rawContentData.textContent && (
                  <div>
                    <h4 className="font-semibold mb-2">Text Content:</h4>
                    {Object.entries(rawContentData.textContent).map(([key, value]) => (
                      <div key={key} className="mb-2">
                        <p className="font-medium">{key}:</p>
                        <p className="text-xs ml-2">{value as string}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer - Screen Only */}
      <div className="print:hidden flex items-center justify-between pt-6 border-t">
        <Button variant="outline" onClick={onBack}>
          Back to Profile
        </Button>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Print Report
          </Button>
          <Button onClick={handleExportPDF} disabled={isExporting}>
            <Download className="w-4 h-4 mr-2" />
            {isExporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        </div>
      </div>

      {/* Print-specific CSS */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact;
          }
          .print\\:block {
            display: block !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .page-break {
            page-break-before: always;
          }
          @page {
            margin: 1in;
            size: letter;
          }
        }
      `}</style>
    </div>
  );
}